/**
 * SuccessWare BookIt Test Page
 * 
 * Test page for pushing bookings to SuccessWare with all fields visible
 */

import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Link } from 'react-router-dom';
import { ChevronRight, Send, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { successWareBookItService } from '@/lib/services/SuccessWareBookItService';
import type { ServiceNoteFields } from '@/lib/config/successware-field-mappings';
import { getCompanyMapping } from '@/lib/config/successware-field-mappings';

export default function SuccessWareBookItTestPage() {
  const { theme } = useTheme();
  const [companyId, setCompanyId] = useState('hales-ac');
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState<any>(null);

  // Customer Info
  const [customerInfo, setCustomerInfo] = useState({
    firstName: 'John',
    lastName: 'Smith',
    phoneNumber: '+15615551234',
    email: 'john.smith@example.com',
    address1: '123 Main Street',
    address2: 'Apt 4B',
    city: 'Boca Raton',
    state: 'FL',
    zipCode: '33431',
  });

  // Service Notes (14 fields)
  const [serviceNotes, setServiceNotes] = useState<ServiceNoteFields>({
    appointmentDate: new Date().toISOString().split('T')[0],
    arrivalTimeWindow: '8am-12pm',
    bookedBy: 'Test Agent',
    serviceRequired: 'AC not cooling properly',
    customerIssues: 'Unit running but no cold air coming out',
    amountQuoted: '$350.00',
    equipmentAge: '8 years',
    numberOfThermostats: '2',
    airHandlerLocation: 'Attic',
    condenserLocation: 'Side yard',
    ladderNeeded: 'yes',
    ladderSize: '24ft',
    accessInstructions: 'Gate code: 1234, park in driveway',
    potentialHazards: 'Large dog in backyard, please call first',
  });

  const handlePush = async () => {
    setPushing(true);
    setResult(null);

    try {
      const pushResult = await successWareBookItService.pushBooking({
        companyId,
        customerInfo,
        serviceNotes,
        callSid: 'TEST-' + Date.now(),
      });

      setResult(pushResult);
    } catch (error: any) {
      setResult({
        success: false,
        message: error.message || 'Push failed',
        error: error.toString(),
      });
    } finally {
      setPushing(false);
    }
  };

  const handlePreview = () => {
    const preview = successWareBookItService.previewBookingNotes(companyId, serviceNotes);
    alert(preview || 'No preview available');
  };

  const mapping = getCompanyMapping(companyId);

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: theme.colors.bgPage }}>
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm mb-6" style={{ color: theme.colors.textSecondary }}>
        <Link to="/" className="hover:text-white">Home</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/integrations" className="hover:text-white">Integrations</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/integrations/successware" className="hover:text-white">SuccessWare</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-white">BookIt Test</span>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.textPrimary }}>
          SuccessWare BookIt Test
        </h1>
        <p style={{ color: theme.colors.textSecondary }}>
          Test pushing bookings to SuccessWare with all fields visible
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Input Fields */}
        <div className="space-y-6">
          {/* Company Selection */}
          <div className="p-6 rounded-lg" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>
              Company Selection
            </h2>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                Company
              </label>
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: theme.colors.bgPage,
                  color: theme.colors.textPrimary,
                  border: `1px solid ${theme.colors.borderColor}`,
                }}
              >
                <option value="hales-ac">Hales AC</option>
                <option value="climate-control">Climate Control Services</option>
                <option value="abraham-ac">Abraham AC</option>
              </select>
              {mapping && (
                <div className="mt-3 p-3 rounded" style={{ backgroundColor: theme.colors.bgPage }}>
                  <div className="text-xs space-y-1" style={{ color: theme.colors.textSecondary }}>
                    <div><strong>Primary Tenant:</strong> {mapping.tenantId}</div>
                    {mapping.secondaryTenantId && (
                      <div><strong>Secondary Tenant:</strong> {mapping.secondaryTenantId}</div>
                    )}
                    <div><strong>Department ID:</strong> {mapping.defaultDepartmentId}</div>
                    <div><strong>Job Type:</strong> {mapping.defaultJobType}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div className="p-6 rounded-lg" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>
              Customer Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  First Name * <code className="text-xs ml-1 opacity-60">firstName</code>
                </label>
                <input
                  type="text"
                  value={customerInfo.firstName}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, firstName: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Last Name * <code className="text-xs ml-1 opacity-60">lastName</code>
                </label>
                <input
                  type="text"
                  value={customerInfo.lastName}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, lastName: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Phone Number * <code className="text-xs ml-1 opacity-60">phoneNumber</code>
                </label>
                <input
                  type="text"
                  value={customerInfo.phoneNumber}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phoneNumber: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                  placeholder="+15615551234"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Email <code className="text-xs ml-1 opacity-60">email</code>
                </label>
                <input
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Address 1 * <code className="text-xs ml-1 opacity-60">address1</code>
                </label>
                <input
                  type="text"
                  value={customerInfo.address1}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, address1: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Address 2 <code className="text-xs ml-1 opacity-60">address2</code>
                </label>
                <input
                  type="text"
                  value={customerInfo.address2}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, address2: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  City * <code className="text-xs ml-1 opacity-60">city</code>
                </label>
                <input
                  type="text"
                  value={customerInfo.city}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  State * <code className="text-xs ml-1 opacity-60">state</code>
                </label>
                <input
                  type="text"
                  value={customerInfo.state}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, state: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                  placeholder="FL"
                  maxLength={2}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Zip Code * <code className="text-xs ml-1 opacity-60">zipCode</code>
                </label>
                <input
                  type="text"
                  value={customerInfo.zipCode}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, zipCode: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                  placeholder="33431"
                />
              </div>
            </div>
          </div>

          {/* Service Notes (14 Fields) */}
          <div className="p-6 rounded-lg" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>
              Service Notes (14 Fields)
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Appointment Date <code className="text-xs ml-1 opacity-60">appointmentDate</code>
                </label>
                <input
                  type="date"
                  value={serviceNotes.appointmentDate || ''}
                  onChange={(e) => setServiceNotes({ ...serviceNotes, appointmentDate: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Arrival Time Window <code className="text-xs ml-1 opacity-60">arrivalTimeWindow</code>
                </label>
                <input
                  type="text"
                  value={serviceNotes.arrivalTimeWindow || ''}
                  onChange={(e) => setServiceNotes({ ...serviceNotes, arrivalTimeWindow: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                  placeholder="e.g., 8am-12pm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Booked By <code className="text-xs ml-1 opacity-60">bookedBy</code>
                </label>
                <input
                  type="text"
                  value={serviceNotes.bookedBy || ''}
                  onChange={(e) => setServiceNotes({ ...serviceNotes, bookedBy: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Service Required <code className="text-xs ml-1 opacity-60">serviceRequired</code>
                </label>
                <textarea
                  value={serviceNotes.serviceRequired || ''}
                  onChange={(e) => setServiceNotes({ ...serviceNotes, serviceRequired: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Customer Issues <code className="text-xs ml-1 opacity-60">customerIssues</code>
                </label>
                <textarea
                  value={serviceNotes.customerIssues || ''}
                  onChange={(e) => setServiceNotes({ ...serviceNotes, customerIssues: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                    Amount Quoted <code className="text-xs ml-1 opacity-60">amountQuoted</code>
                  </label>
                  <input
                    type="text"
                    value={serviceNotes.amountQuoted || ''}
                    onChange={(e) => setServiceNotes({ ...serviceNotes, amountQuoted: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      color: theme.colors.textPrimary,
                      border: `1px solid ${theme.colors.borderColor}`,
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                    Equipment Age <code className="text-xs ml-1 opacity-60">equipmentAge</code>
                  </label>
                  <input
                    type="text"
                    value={serviceNotes.equipmentAge || ''}
                    onChange={(e) => setServiceNotes({ ...serviceNotes, equipmentAge: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      color: theme.colors.textPrimary,
                      border: `1px solid ${theme.colors.borderColor}`,
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="
block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Number of Thermostats <code className="text-xs ml-1 opacity-60">numberOfThermostats</code>
                </label>
                <input
                  type="text"
                  value={serviceNotes.numberOfThermostats || ''}
                  onChange={(e) => setServiceNotes({ ...serviceNotes, numberOfThermostats: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                    Air Handler Location <code className="text-xs ml-1 opacity-60">airHandlerLocation</code>
                  </label>
                  <input
                    type="text"
                    value={serviceNotes.airHandlerLocation || ''}
                    onChange={(e) => setServiceNotes({ ...serviceNotes, airHandlerLocation: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      color: theme.colors.textPrimary,
                      border: `1px solid ${theme.colors.borderColor}`,
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                    Condenser Location <code className="text-xs ml-1 opacity-60">condenserLocation</code>
                  </label>
                  <input
                    type="text"
                    value={serviceNotes.condenserLocation || ''}
                    onChange={(e) => setServiceNotes({ ...serviceNotes, condenserLocation: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      color: theme.colors.textPrimary,
                      border: `1px solid ${theme.colors.borderColor}`,
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                    Ladder Needed <code className="text-xs ml-1 opacity-60">ladderNeeded</code>
                  </label>
                  <select
                    value={serviceNotes.ladderNeeded || 'unknown'}
                    onChange={(e) => setServiceNotes({ ...serviceNotes, ladderNeeded: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      color: theme.colors.textPrimary,
                      border: `1px solid ${theme.colors.borderColor}`,
                    }}
                  >
                    <option value="unknown">Unknown</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                    Ladder Size <code className="text-xs ml-1 opacity-60">ladderSize</code>
                  </label>
                  <input
                    type="text"
                    value={serviceNotes.ladderSize || ''}
                    onChange={(e) => setServiceNotes({ ...serviceNotes, ladderSize: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      color: theme.colors.textPrimary,
                      border: `1px solid ${theme.colors.borderColor}`,
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Access Instructions <code className="text-xs ml-1 opacity-60">accessInstructions</code>
                </label>
                <textarea
                  value={serviceNotes.accessInstructions || ''}
                  onChange={(e) => setServiceNotes({ ...serviceNotes, accessInstructions: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
                  Potential Hazards <code className="text-xs ml-1 opacity-60">potentialHazards</code>
                </label>
                <textarea
                  value={serviceNotes.potentialHazards || ''}
                  onChange={(e) => setServiceNotes({ ...serviceNotes, potentialHazards: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                  }}
                  rows={2}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Actions & Results */}
        <div className="space-y-6">
          {/* Actions */}
          <div className="p-6 rounded-lg" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>
              Actions
            </h2>
            <div className="space-y-3">
              <button
                onClick={handlePreview}
                className="w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: theme.colors.bgPage,
                  color: theme.colors.textPrimary,
                  border: `1px solid ${theme.colors.borderColor}`,
                }}
              >
                Preview Work Request Notes
              </button>
              <button
                onClick={handlePush}
                disabled={pushing}
                className="w-full px-4 py-3 rounded-lg text-white text-sm font-medium flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
                style={{ backgroundColor: '#10b981' }}
              >
                {pushing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Pushing to SuccessWare...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Push to SuccessWare
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Result */}
          {result && (
            <div
              className="p-6 rounded-lg"
              style={{
                backgroundColor: result.success ? '#10b98120' : '#ef444420',
                border: `1px solid ${result.success ? '#10b981' : '#ef4444'}`,
              }}
            >
              <div className="flex items-start gap-3 mb-4">
                {result.success ? (
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2" style={{ color: result.success ? '#10b981' : '#ef4444' }}>
                    {result.success ? 'Success!' : 'Failed'}
                  </h3>
                  <p className="text-sm" style={{ color: result.success ? '#10b981' : '#ef4444' }}>
                    {result.message}
                  </p>
                </div>
              </div>

              {result.success && (
                <div className="space-y-2 text-sm" style={{ color: theme.colors.textSecondary }}>
                  {result.jobNumber && (
                    <div>
                      <strong>Job Number:</strong> {result.jobNumber}
                    </div>
                  )}
                  {result.jobId && (
                    <div>
                      <strong>Job ID:</strong> {result.jobId}
                    </div>
                  )}
                  {result.serviceAccountId && (
                    <div>
                      <strong>Service Account ID:</strong> {result.serviceAccountId}
                    </div>
                  )}
                </div>
              )}

              {result.error && (
                <div className="mt-3 p-3 rounded text-xs font-mono" style={{ backgroundColor: theme.colors.bgPage, color: theme.colors.textSecondary }}>
                  {result.error}
                </div>
              )}
            </div>
          )}

          {/* Field Mapping Info */}
          <div className="p-6 rounded-lg" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
            <h2 className="text-xl font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>
              Field Mapping Info
            </h2>
            <div className="text-xs space-y-2" style={{ color: theme.colors.textSecondary }}>
              <div><strong>Company:</strong> {mapping?.companyName}</div>
              <div><strong>Format:</strong> {companyId === 'hales-ac' ? 'Verbose line-by-line' : companyId === 'climate-control' ? 'Compact pipe-separated' : 'Structured sections'}</div>
              <div className="mt-4">
                <strong>Pushes To:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Service Account (creates if new)</li>
                  <li>Job with all 14 service notes</li>
                  <li>Work request notes (formatted)</li>
                  <li>Scheduling (if appointment date set)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
